/**
 * ProviderToolbar — sort dropdown + search input + custom filter chips row.
 *
 * Layout rules (mobile-first):
 *   - On <sm: search takes full row; sort + extras scroll horizontally below.
 *   - On sm+: search and sort share the same row.
 *   - extraFilters slot appended after the sort dropdown (both breakpoints).
 */

import type { ReactNode } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { SortKey } from './types';

interface ProviderToolbarProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  sortKey: SortKey;
  onSortChange: (k: SortKey) => void;
  sortKeys: SortKey[];
  extraFilters?: ReactNode;
  resultCount: number;
  searchable: boolean;
}

const SORT_LABELS: Record<SortKey, string> = {
  price: 'Price',
  speed: 'Speed',
  quality: 'Quality',
  name: 'Name',
};

export function ProviderToolbar({
  searchQuery,
  onSearchChange,
  sortKey,
  onSortChange,
  sortKeys,
  extraFilters,
  resultCount,
  searchable,
}: ProviderToolbarProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col sm:flex-row gap-2">
      {/* Search — full width on mobile, flex-1 on sm+ */}
      {searchable && (
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('providerPicker.searchPlaceholder', 'Search...')}
            className="w-full min-h-[44px] pl-8 pr-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          />
          {resultCount > 0 && (
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-white/30 pointer-events-none">
              {resultCount}
            </span>
          )}
        </div>
      )}

      {/* Sort + extra filters: horizontal scroll on small screens */}
      <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-0.5 sm:pb-0 shrink-0">
        {/* Sort dropdown */}
        {sortKeys.length > 1 && (
          <select
            value={sortKey}
            onChange={(e) => onSortChange(e.target.value as SortKey)}
            className="min-h-[44px] bg-black/30 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white/80 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors cursor-pointer"
            aria-label={t('providerPicker.sortBy', 'Sort by')}
          >
            {sortKeys.map((k) => (
              <option key={k} value={k}>
                {SORT_LABELS[k]}
              </option>
            ))}
          </select>
        )}

        {/* Injected filter chips (e.g. "Free Only") */}
        {extraFilters}
      </div>
    </div>
  );
}

export default ProviderToolbar;
